// The npm `buffer` package, not `node:buffer`: iconv-lite needs a Buffer and this module is
// bundled for the browser, where the `node:` specifier does not resolve.
import { Buffer } from 'buffer';
import iconv from 'iconv-lite';

/** A distinct `CateTitle` value and how many records carry it. */
export interface DbfCategory {
  value: string;
  count: number;
}

/** Most per-category color picks the API accepts on one upload; a longer list is rejected with a 400. */
export const MAX_CATEGORY_COLORS = 500;

/** Longest label the custom-data API accepts; a longer one is rejected with a 400. */
export const MAX_CUSTOM_DATA_LABEL_LENGTH = 35;

const FIELD_DESCRIPTORS_START = 32;
const FIELD_DESCRIPTOR_BYTES = 32;
const FIELD_TERMINATOR = 0x0d;
const FIELD_NAME_BYTES = 11;
const FIELD_TYPE_OFFSET = 11;
const FIELD_LENGTH_OFFSET = 16;
const CATE_TITLE_FIELD = 'CateTitle';

// The type codes `parsedbf` converts away from text during ingest; every other code stays a string.
const NON_TEXT_FIELD_TYPES = new Set(['N', 'F', 'O', 'D', 'L']);

interface DbfField {
  name: string;
  /** Single-letter DBF type code, e.g. `C` for character or `N` for numeric. */
  type: string;
  /** Byte offset within a record, past the leading deletion flag. */
  offset: number;
  length: number;
}

/**
 * Builds the text decoder for a DBF attribute table.
 *
 * Reproduces `parsedbf`'s Node decoder — the path `shpjs` takes during ingest — including its
 * `encodingExists` check, its `slice(5)` retry for labels like `ANSI 1252`, its UTF-8 default, and
 * the trailing NUL-strip and trim. `iconv-lite` is imported directly rather than through
 * `parsedbf` because that package swaps in a `TextDecoder` implementation under the `browser`
 * condition, which disagrees with the Node one on several code pages. The UTF-8 default uses
 * `TextDecoder` because the npm `buffer` shim emits one replacement character per stray byte
 * where Node emits one per truncated sequence.
 */
export function createDbfDecoder(encoding?: string): (bytes: Uint8Array) => string {
  const clean = (out: string) => out.replace(/\0/g, '').trim();
  const label = resolveEncodingLabel(encoding);
  if (!label) {
    const utf8 = new TextDecoder('utf-8');
    return (bytes) => clean(utf8.decode(bytes));
  }
  return (bytes) => clean(iconv.decode(Buffer.from(bytes), label));
}

function resolveEncodingLabel(encoding?: string): string | undefined {
  if (!encoding) return undefined;
  if (iconv.encodingExists(encoding)) return encoding;
  const sliced = encoding.length > 5 ? encoding.slice(5) : undefined;
  return sliced && iconv.encodingExists(sliced) ? sliced : undefined;
}

/** Normalizes a raw `CateTitle` to its category key; null/undefined/empty all become `''`. */
export function coalesceCateTitle(raw: unknown): string {
  return typeof raw === 'string' && raw.length > 0 ? raw : '';
}

/** Category order: most features first, then value ascending, so a persisted legend is stable. */
export function compareCategoryOrder(a: { value: string; count: number }, b: { value: string; count: number }): number {
  return b.count - a.count || a.value.localeCompare(b.value);
}

function readFieldDescriptors(bytes: Uint8Array, view: DataView, headerLength: number): DbfField[] {
  const fields: DbfField[] = [];
  const ascii = createDbfDecoder();
  // Records open with a one-byte deletion flag, so field data starts one byte in.
  let offset = 1;
  for (let at = FIELD_DESCRIPTORS_START; at + FIELD_DESCRIPTOR_BYTES <= headerLength; at += FIELD_DESCRIPTOR_BYTES) {
    if (view.getUint8(at) === FIELD_TERMINATOR) break;
    fields.push({
      name: ascii(bytes.subarray(at, at + FIELD_NAME_BYTES)),
      type: String.fromCharCode(view.getUint8(at + FIELD_TYPE_OFFSET)),
      offset,
      length: view.getUint8(at + FIELD_LENGTH_OFFSET),
    });
    offset += fields[fields.length - 1].length;
  }
  return fields;
}

/**
 * Reads one `CateTitle` per record from a DBF attribute table.
 *
 * `cpg` is the shapefile's `.cpg` sidecar contents, if present. A table without the column, or
 * with a numeric, date or logical one, yields one empty value per record, which collapses to the
 * single uncategorized group ingest computes for the same file. Throws on a truncated header.
 */
export function readDbfCateTitles(dbf: Uint8Array, cpg?: string): string[] {
  const bytes = dbf;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerLength = view.getUint16(8, true);
  const recordLength = view.getUint16(10, true);
  // A corrupt header can claim far more records than the file holds, and a zero-width record
  // leaves every read at the same offset.
  const capacity = recordLength === 0 ? 0 : Math.floor((bytes.length - headerLength) / recordLength);
  const recordCount = Math.min(view.getUint32(4, true), Math.max(0, capacity));

  const field = readFieldDescriptors(bytes, view, headerLength).find((f) => f.name === CATE_TITLE_FIELD);
  // Ingest parses a non-text column into a number, date or boolean, each of which coalesces to the
  // uncategorized group, so reading its digits as text would offer categories nothing persists.
  if (!field || NON_TEXT_FIELD_TYPES.has(field.type)) return new Array(recordCount).fill('');

  const decode = createDbfDecoder(cpg);
  const values: string[] = [];
  for (let i = 0; i < recordCount; i++) {
    const start = headerLength + i * recordLength + field.offset;
    if (start + field.length > bytes.length) break;
    values.push(decode(bytes.subarray(start, start + field.length)));
  }
  return values;
}

/** Counts distinct values in the order the persisted category list uses. */
export function tallyCateTitles(values: readonly string[]): DbfCategory[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    const key = coalesceCateTitle(value);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()].map(([value, count]) => ({ value, count })).sort(compareCategoryOrder);
}
